<template>
    <div>
        <b-row>
            <b-col cols="12" lg="4">
                <card plain class="filters">
                    <b-form @submit.prevent="search">
                        <b-form-group label="NPI">
                            <b-input v-model="npi" size="sm" type="search" autocomplete="off" />
                        </b-form-group>
                        <b-form-group label="NCPDP Id">
                            <b-input v-model="ncpdp" size="sm" type="search" autocomplete="off" />
                        </b-form-group>
                        <b-form-group label="DEA Number">
                            <b-input v-model="dea" size="sm" type="search" autocomplete="off" />
                        </b-form-group>
                        <b-form-group label="Organization Name">
                            <b-input v-model="name" size="sm" type="search" autocomplete="off" />
                        </b-form-group>
                        <b-form-group label="Fax Number">
                            <b-input v-model="fax" size="sm" type="search" autocomplete="off" />
                        </b-form-group>
                        <b-form-group label="Phone Number">
                            <b-input v-model="phone" size="sm" type="search" autocomplete="off" />
                        </b-form-group>
                        <b-form-group label="State">
                            <fg-select v-model="state" :options="stateOptions" clearable />
                        </b-form-group>
                        <b-form-group label="City">
                            <b-input v-model="city" size="sm" type="search" autocomplete="off" />
                        </b-form-group>
                        <b-form-group label="Postal Code">
                            <b-input v-model="postalCode" size="sm" type="search" autocomplete="off" />
                        </b-form-group>
                        <div class="mt-4">
                            <b-button variant="primary" block type="submit">
                                <i class="fas fa-search" />
                                Search
                            </b-button>
                        </div>
                    </b-form>
                </card>
            </b-col>
            <b-col cols="12" lg="8">
                <div v-if="loading" class="d-flex flex-column align-items-center" style="gap: 15px;">
                    <b-spinner :show="true" />
                    <div>Searching...</div>
                </div>
                <template v-else>
                    <card v-if="didSearch && !resultsData.length" class="text-center" plain>
                        No results found.
                    </card>

                    <card v-for="i in resultsData" :key="i.organization.organizationID" plain>
                        <div class="d-flex align-items-center">
                            <div class="flex-fill d-flex flex-column" style="gap: 2px;">
                                <div>
                                    <strong>
                                        {{ i.organization.organizationName }}
                                    </strong>
                                    <template v-if="i.organization.identification.storeNumber">
                                        ({{ `#${i.organization.identification.storeNumber}` }})
                                    </template>
                                </div>
                                <div>
                                    <i class="fa fa-map-pin fa-fw" />
                                    {{ i.organization.address?.addressLine1 + ", " + i.organization.address?.city + ", "
                                        +
                                        i.organization.address?.stateProvince + " " + i.organization.address?.postalCode }}
                                </div>
                                <div class="d-flex" style="gap: 15px;">
                                    <div v-if="i.organization.communicationNumbers?.primaryTelephone?.number">
                                        <i class="fa fa-phone fa-fw" />
                                        Phone: {{ i.organization.communicationNumbers?.primaryTelephone?.number }}
                                    </div>
                                    <div v-if="i.organization.communicationNumbers?.fax?.number">
                                        <i class="fa fa-fax fa-fw" />
                                        Fax: {{ i.organization.communicationNumbers?.fax?.number }}
                                    </div>
                                </div>
                                <div v-if="i.organization.pharmacyHoursOfOperation?.trim()">
                                    <small>
                                        <i class="fa fa-clock" />
                                        Hours: {{ i.organization.pharmacyHoursOfOperation }}
                                    </small>
                                </div>
                                <div class="d-flex" style="gap: 15px;">
                                    <small v-if="i.organization.identification.npi">
                                        NPI: {{ i.organization.identification.npi }}
                                    </small>
                                    <small v-if="i.organization.identification.deaNumber">
                                        DEA: {{ i.organization.identification.deaNumber }}
                                    </small>
                                    <small v-if="i.organization.identification.ncpdpid">
                                        NCPDP: {{ i.organization.identification.ncpdpid }}
                                    </small>
                                </div>

                                <div class="d-flex flex-wrap mt-1" style="gap: 6px;">
                                    <b-badge v-if="i.supportsRxTransfer" variant="primary">
                                        <i class="fa fa-bolt"></i>
                                        Electronic RxTransfer
                                    </b-badge>
                                    <b-badge
                                        v-if="i.directoryInformation.serviceLevels?.some(sl => sl.serviceLevelName === 'ControlledSubstance')"
                                        variant="primary">
                                        Controlled Substance
                                    </b-badge>
                                </div>
                            </div>
                            <b-button variant="success" style="align-self: stretch;" @click="$emit('input', i)">
                                <i class="fa fa-plus"></i>
                            </b-button>
                        </div>
                    </card>
                </template>
            </b-col>
        </b-row>
    </div>
</template>

<script lang="ts">
import { ValidValues } from '@/data/ValidValues';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import axios from 'axios';
import { Component, Vue } from 'vue-property-decorator';

@Component({
    name: "ReportsComponent",
    components: {

    }
})
export default class PharmacyDirectorySearch extends Vue {

    protected npi: string = "";
    protected phone: string = "";
    protected fax: string = "";
    protected postalCode: string = "";
    protected city: string = "";
    protected state: string = "";
    protected ncpdp: string = "";
    protected dea: string = "";
    protected name: string = "";

    protected resultsData: any[] = [];

    protected didSearch = false;
    protected loading = false;

    get stateOptions() {
        return ValidValues.addressStates;
    }

    async search() {
        try {
            this.resultsData = [];
            this.loading = true;
            const resp = await axios.get("/StoredPharmacy/directory-search", {
                params: {
                    npi: this.npi,
                    phone: this.phone,
                    fax: this.fax,
                    postalCode: this.postalCode,
                    city: this.city,
                    state: this.state,
                    ncpdpId: this.ncpdp,
                    dea: this.dea,
                    name: this.name
                }
            });
            this.resultsData = resp.data;
            this.didSearch = true;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.loading = false;
        }
    }

}

</script>

<style scoped>
.filters {
    position: sticky;
    top: 0;
}
</style>